import illusCommunity from "images/illus_community.svg";
import ErrorPage from "pages/error-page";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import http from "services/http";
import { ECode } from "ztypes/error-code";
import iconCheck from "images/icon_check.svg";

import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { zoomState } from "state";
import api from "services/http-common";

interface StepData {
  complete: string[];
  un_complete: string[];
  communityCount: number | 0;
  communityQuota: number | 0;
}

interface IResponse {
  error_code: any;
  data?: any;
}

const LandingPage: React.FC<{}> = () => {
  const navigate = useNavigate();
  const zoom = useRecoilValue(zoomState);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<IResponse>({
    error_code: -1001,
  });
  const [searchParams] = useSearchParams();
  const encryptGid = searchParams.get("gid");
  useEffect(() => {
    document.title = t("lp.header.title");
    api
      .get<IResponse>(`/status`, {
        params: { gid: encryptGid },
      })
      .then((resp) => {
        const data = resp.data;
        if (resp.data && resp.data.data) {
          setStatus({ error_code: data.error_code, data: data.data });
          setIsLoading(false);
        } else {
          navigate("/nf");
        }
      })
      .catch(() => navigate("/nf"));
  }, []);

  const handleBottomBtn = (action: string) => {
    switch (action) {
      case "ekyc":
        return verifyAccount();
      case "regis_zba":
        return upgradeZba();
      case "upgrade_community":
        return upgradeCommunity();
    }
  };

  const upgradeZba = () => {
    window.location.href = `/upgrade-zba${window.location.search}`;
  };

  const verifyAccount = () => {
    window.location.href = `${process.env.REACT_APP_PM_EKYC_URL}&gid=${encryptGid}`;
  };

  const upgradeCommunity = async () => {
    const encryptGid = searchParams.get("gid");
    const resp = await http.upgrateCommunity(encryptGid);
    if (resp) {
      if (resp.error_code == ECode.SUCCESS) {
        navigate(`/success${window.location.search}`);
      } else {
        setStatus({ error_code: resp.error_code, data: resp.data });
      }
    } else {
      navigate("/nf");
    }
  };

  if (isLoading || !status.data) {
    return <></>;
  }
  if (status.error_code == ECode.ALREADY_WAS_COMMUNITY) {
    navigate(`/success${window.location.search}`);
  }
  if (status.error_code != ECode.SUCCESS) {
    return <ErrorPage ecode={status.error_code} data={status.data} />;
  }
  const stepData: StepData = status.data;
  return (
    <div className="pt-12 short:pt-4 px-6 h-screen flex flex-col flex-grow items-center self-stretch justify-between text-size-adjust-auto">
      <div className="flex flex-col" style={{ zoom: zoom }}>
        <div className="flex flex-col items-center self-stretch">
          <img
            className="w-32 h-32 short:w-32 short:h-28"
            src={illusCommunity}
            alt=""
          />
          <div className="mt-6 short:mt-3 flex flex-col items-center self-stretch ">
            <div className="text-center font-medium text-[22px] leading-[26px]">
              {t("lp.header.title")}
            </div>
            <div
              className="mt-4 short:mt-2 text-center font-normal text-[15px] leading-[20px]"
              style={{ color: "#6F7071" }}
            >
              {`${t("lp.header.sub_title")}. `}
              <a
                id="idHrefLearnMore"
                href="https://help.zalo.me/huong-dan/chuyen-muc/tro-chuyen-nhom/tro-chuyen-cong-dong-them-ket-noi-them-quyen-rieng-tu/"
                className="font-medium"
                style={{ color: "#006AF5" }}
              >
                {t("lp.header.learn_more")}
              </a>
            </div>
          </div>
        </div>
        <div className="mt-8 short:mt-4 px-4 py-6 rounded-xl flex flex-col bg-neutral-100">
          <div className="font-normal text-[15px] leading-[20px] text-neutral-500">
            {t("lp.step.title", {
              n_steps: stepData.complete.length + stepData.un_complete.length,
            })}
          </div>
          <div className="mt-4 flex flex-col">
            {stepData.complete.map((step: string, index: number) => (
              <div key={index} className={`flex text-neutral-500`}>
                <div className="flex flex-col items-center">
                  <img className="w-6 h-6" src={iconCheck} alt="" />
                  <div className="my-1 w-[1.5px] h-10 short:h-5 rounded-full bg-zinc-500"></div>
                </div>
                <div className="ml-3 flex flex-col items-start">
                  <div className="font-medium text-[16px] leading-[22px]">
                    {t(`lp.step.${step}.title.success`)}
                  </div>
                </div>
              </div>
            ))}
            {stepData.un_complete.map((step: string, index: number) => (
              <div
                key={index}
                className={`flex ${index > 0 ? "text-zinc-400" : ""}`}
              >
                <div className="flex flex-col items-center">
                  <div
                    className={`w-6 h-6 flex flex-col justify-center items-center rounded-full border-2 border-solid ${
                      index > 0
                        ? "border-zinc-400"
                        : step == "pending_ekyc"
                        ? "border-yellow-400"
                        : "border-blue-600"
                    }`}
                  >
                    <div className="left-[10px] top-[5px] font-normal text-[10px] leading-[14px]">
                      {stepData.complete.length + index + 1}
                    </div>
                  </div>
                  {index < stepData.un_complete.length - 1 && (
                    <div className="my-1 w-[1.5px] h-10 short:h-5 rounded-full bg-zinc-500"></div>
                  )}
                </div>
                <div className="ml-3 flex flex-col items-start">
                  <div className="font-medium text-[16px] leading-[22px]">
                    {t(`lp.step.${step}.title`)}
                  </div>
                  {step == "pending_ekyc" && (
                    <div className="mt-0.5 font-normal text-[14px] leading-[18px]">
                      {t(`lp.step.pending_ekyc.sub_title`)}
                    </div>
                  )}
                  {step == "upgrade_community" &&
                    stepData.un_complete.length == 1 && (
                      <div className="mt-0.5 font-normal text-[14px] leading-[18px] text-neutral-500">
                        <Trans
                          i18nKey="lp.step.upgrade_community.sub_title"
                          values={{
                            count: stepData.communityCount,
                            communityQuota: stepData.communityQuota,
                          }}
                          components={{
                            font500: <span className="font-medium"></span>,
                          }}
                        />
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className={`mt-2 mb-6 flex flex-col items-start self-stretch rounded-full ${
          stepData.un_complete[0] == "pending_ekyc"
            ? "bg-gray-300"
            : "bg-blue-600"
        }`}
        style={{ zoom: zoom }}
        onClick={() => handleBottomBtn(stepData.un_complete[0])}
      >
        <div className="flex flex-col h-12 py-3 justify-center items-center self-stretch font-medium text-[15px] leading-[20px] text-white">
          {t(
            `lp.btn.${
              stepData.un_complete[0] == "pending_ekyc"
                ? "continue_step_" + (stepData.complete.length + 2)
                : stepData.un_complete[0]
            }`
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
